<script lang="ts">
  import * as Store from '@/store';
  import {createEventDispatcher, getContext, onDestroy, onMount} from 'svelte';

  import {CloseButton, LockedModal} from '@/components';
  import {isBetaUser as isBetaUserFunc} from '@/lib/beta/beta';
  import {IntegrationService} from '@/services';
  import {Features, UploadSource} from '@/types/common';
  import {Integration} from '@/types/integrations';
  import type {Video} from '@/types/project';
  import {MemberShipType, type User} from '@/types/user';
  import {canAccess} from '@/utils/common';
  import Swal from 'sweetalert2';

  export let isUploadDropdownOpen = false;
  export let isUploadLiveStreamDialogOpen = false;
  export let isUploadYoutubeDialogOpen = false;
  export let isUploadLocalDialogOpen = false;
  export let isUploading = false;

  let isCreditZero = false; // for some reason only youtube upload does not have isCreditZero
  let user: User;
  let videos: Video[] = [];
  let integrations: Array<Integration> = [];
  let isPaidUser = false;
  let isBetaUser = false;
  const simpleModal: {open: (c: any, o: any, oo: any) => void} = getContext('simple-modal');
  const isAdmin = window.uData && window.uData?.is_admin;

  const creditSubscription = Store.credit.subscribe(credit => (isCreditZero = credit.amount <= 0));
  const userSubscription = Store.user.subscribe(value => (user = value));
  const videosSubscription = Store.videos.subscribe(value => (videos = value.files));
  const dispatch = createEventDispatcher();
  $: {
    isPaidUser = user.membership !== MemberShipType.FREE || isAdmin || !isCreditZero;
  }

  function openLockedModal() {
    if (!simpleModal) return console.error('simple modal not found');
    simpleModal.open(
      LockedModal,
      {forType: 'video'},
      {
        classWindow: 'relative w-full max-h-full my-2 mx-auto rounded shadow-md bg-white max-w-xl',
        closeOnOuterClick: false,
        closeButton: CloseButton
      }
    );
  }

  function warnUserCreditZero() {
    Swal.fire({
      title: 'Cannot Upload',
      text: 'Oh no it seems you have zero credits left. Please top up on your credits before proceeding',
      icon: 'error',
      confirmButtonText: 'Ok'
    });
  }

  function isDisabled() {
    Swal.fire({
      titleText: 'Problem with YouTube',
      html: `
            <div class="flex justify-center flex-col">
                <p class="block mb-4">YouTube uploads are unstable at the moment due to updates on YouTube's end. We are working on a fix for this. If your upload fails, please consider uploading from a different source or follow these steps:
                </p>
                <iframe height="400" src="https://www.youtube.com/embed/-QKpjZfBVdU" title="How to download your own video from Youtube and Facebook" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
            </div>`,
      icon: 'warning',
      width: 700,
      confirmButtonText: 'Proceed with Upload'
    });
  }

  function setSource(source: UploadSource) {
    if (source === UploadSource.LIVESTREAM) {
      isUploadLiveStreamDialogOpen = true;
    } else if (source === UploadSource.LOCAL) {
      isUploadLocalDialogOpen = true;
    } else {
      isUploadYoutubeDialogOpen = true;
    }

    dispatch('set-source', {source: source});
  }

  function setIntegration(integration: any) {
    dispatch('set-integration', {integration: integration});
  }

  onMount(async () => {
    try {
      isBetaUser = await isBetaUserFunc();
    } catch (error) {
      console.log('failed to get isBetaUser', error);
    }
    // Get Integrations
    integrations = await new IntegrationService().get();
  });

  onDestroy(() => {
    creditSubscription();
    userSubscription();
    videosSubscription();
  });

  const isYtdlEnabled = Store.featuresStore.isEnabled('ytdl');
</script>

<div class="relative">
  <button
    on:click={() => (isUploadDropdownOpen = !isUploadDropdownOpen)}
    class="{isUploading
      ? 'disabled cursor-not-allowed'
      : 'cursor-pointer'} flex w-full items-center justify-center gap-2 rounded-md bg-gradient-to-r from-primary to-secondary px-4 py-2 text-white shadow lg:w-auto"
    disabled={isUploading}
  >
    <i class="fa-solid fa-upload"></i>
    <span class="font-bold">Upload Video</span>
  </button>
  <!-- Type Dropdown -->
  <div
    class="{isUploadDropdownOpen
      ? ''
      : 'hidden'} absolute right-0 top-12 z-10 grid w-full gap-2 rounded-lg border border-gray-200 bg-white p-3 text-gray-600 shadow dark:border-zinc-600 dark:bg-zinc-700 dark:text-zinc-200 lg:w-[280px]"
  >
    {#if canAccess(user, Features.LIVESTREAM)}
      <button
        on:click={() =>
          isPaidUser ? (isCreditZero ? warnUserCreditZero() : setSource(UploadSource.LIVESTREAM)) : openLockedModal()}
        class="flex items-center justify-start gap-1 rounded p-2 pr-4 hover:bg-gray-50 dark:hover:bg-zinc-600"
      >
        <i class="fa-solid fa-signal-stream"></i>
        <span class="ml-2 font-medium">Live Stream</span>
      </button>
    {/if}
    <button
      on:click={() =>
        isPaidUser ? (isCreditZero ? warnUserCreditZero() : setSource(UploadSource.YOUTUBE)) : openLockedModal()}
      class="flex items-center justify-start gap-1 rounded p-2 hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-60 dark:hover:bg-zinc-600"
    >
      <i class="fa-brands fa-youtube"></i>
      <span class="ml-2 font-medium">YouTube</span>
      {#if !$isYtdlEnabled}
        <span
          on:click={() => isDisabled()}
          on:keydown={e => e.key === 'Enter' && isDisabled()}
          class="ml-2 inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-sm font-medium text-yellow-500 ring-1 ring-inset ring-yellow-600/10"
          >Unstable <i class="fa-regular fa-circle-question ml-2"></i></span
        >
      {/if}
    </button>
    <button
      on:click={() =>
        isPaidUser ? (isCreditZero ? warnUserCreditZero() : setSource(UploadSource.VIMEO)) : openLockedModal()}
      class="flex items-center justify-start gap-1 rounded p-2 hover:bg-zinc-100 dark:hover:bg-zinc-600"
    >
      <i class="fa-brands fa-vimeo"></i>
      <span class="ml-2 font-medium">Vimeo</span>
    </button>
    <button
      on:click={() =>
        isPaidUser ? (isCreditZero ? warnUserCreditZero() : setSource(UploadSource.FACEBOOK)) : openLockedModal()}
      class="flex items-center justify-start gap-1 rounded p-2 hover:bg-zinc-100 dark:hover:bg-zinc-600"
    >
      <i class="fa-brands fa-facebook"></i>
      <span class="ml-2 font-medium">Facebook</span>
    </button>
    <button
      on:click={() =>
        isPaidUser ? (isCreditZero ? warnUserCreditZero() : setSource(UploadSource.DROPBOX)) : openLockedModal()}
      class="flex items-center justify-start gap-1 rounded p-2 hover:bg-zinc-100 dark:hover:bg-zinc-600"
    >
      <i class="fa-brands fa-dropbox"></i>
      <span class="ml-2 font-medium">Dropbox</span>
    </button>
    <button
      on:click={() =>
        isPaidUser ? (isCreditZero ? warnUserCreditZero() : setSource(UploadSource.GOOGLEDRIVE)) : openLockedModal()}
      class="flex items-center justify-start gap-1 rounded p-2 hover:bg-zinc-100 dark:hover:bg-zinc-600"
    >
      <i class="fa-brands fa-google-drive"></i>
      <span class="ml-2 font-medium">Google Drive</span>
    </button>
    <button
      on:click={() =>
        isPaidUser ? (isCreditZero ? warnUserCreditZero() : setSource(UploadSource.LOCAL)) : openLockedModal()}
      class="flex items-center justify-start gap-1 rounded p-2 hover:bg-zinc-100 dark:hover:bg-zinc-600"
    >
      <i class="fa-solid fa-computer"></i>
      <span class="ml-2 font-medium">From this Device</span>
    </button>
    {#each integrations as integration}
      <button
        on:click={() =>
          isPaidUser ? (isCreditZero ? warnUserCreditZero() : setIntegration(integration)) : openLockedModal()}
        class="flex items-center justify-start gap-1 rounded p-2 hover:bg-zinc-100 dark:hover:bg-zinc-600"
      >
        <svg width="20" height="20">
          <image class="mix-blend-multiply grayscale" xlink:href={integration.icon} width="20" height="20" />
        </svg>
        <span class="ml-2 font-medium">{integration.name}</span>
      </button>
    {/each}
  </div>
</div>
